import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useContext,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Yup from 'yup';

import { AuthContext } from '../../../actions/auth-context';
import { update, show, store } from '../../../actions/base-rest';
import {
  currencyToFloat,
  floatToCurrency,
} from '../../../commons/currency-formatter';
import { indexEnum } from '../../../commons/enum';
import InputCurrencyLabel from '../../../components/unform/input-currency-label';
import InputLabel from '../../../components/unform/input-label';
import SelectLabel from '../../../components/unform/select-label';
import { setBreadcrumb } from '../../../reducers/breadcrumb';
import { GroupData } from '../../../styles/GroupData';

const URL = 'products';

// FEAT-015: tamanhos parametrizados por tipo de produto (listas fixas).
const PHOTO_SIZES = [
  '10x15',
  '13x18',
  '15x21',
  '20x25',
  '20x30',
  '30x30',
  '30x40',
  '60x90',
  '90x120',
];
const SPREAD_SIZES = ['30x21', '40x25', '40x30', '60x30'];

// Tipos que MOSTRAM o select de tamanho da FOTO. Para Revelação digital o
// tamanho é OPCIONAL; para Impressão plotter é obrigatório. FOTOLIVRO tem duas
// formas (decisão 04/07): ÁLBUM (os dois tamanhos obrigatórios) ou ADICIONAL
// de álbum (sem tamanhos + qtde de fotos — ex.: 'Foto Adicional Fotolivro').
const TYPES_WITH_PHOTO_SIZE = [
  'Revelação digital',
  'Impressão plotter',
  'Fotolivro',
];
const TYPES_PHOTO_SIZE_REQUIRED = ['Impressão plotter'];

const toOption = (value) => ({ label: value, value });

const Product = () => {
  const { setLoading } = useContext(AuthContext);

  const dispatch = useDispatch();

  const history = useHistory();

  const params = useParams();

  const formRef = useRef(null);

  const [product, setProduct] = useState({});

  const [optionsUnit, setOptionsUnit] = useState([]);

  const [optionsType, setOptionsType] = useState([]);

  const [selectedUnitType, setSelectedUnitType] = useState([]);

  const [selectedType, setSelectedType] = useState([]);

  const [selectedPhotoSize, setSelectedPhotoSize] = useState(null);

  const [selectedSpreadSize, setSelectedSpreadSize] = useState(null);

  // FEAT-015: qual tamanho é exibido/exigido conforme o tipo selecionado.
  const needsPhotoSize = TYPES_WITH_PHOTO_SIZE.includes(selectedType?.value);

  const photoSizeRequired = TYPES_PHOTO_SIZE_REQUIRED.includes(
    selectedType?.value
  );

  const needsSpreadSize = selectedType?.value === 'Fotolivro';

  const getProduct = useCallback(
    async (id) => {
      setLoading(true);

      const response = await show(URL, id);

      response.price = await floatToCurrency(response.price);

      setSelectedType({
        label: response.type,
        value: response.type,
      });

      setSelectedUnitType({
        label: response.unit.toUpperCase(),
        value: response.unit,
      });

      setSelectedPhotoSize(
        response.photo_size ? toOption(response.photo_size) : null
      );

      setSelectedSpreadSize(
        response.spread_size ? toOption(response.spread_size) : null
      );

      formRef.current.setFieldValue('price', response.price);

      setProduct(response);

      setLoading(false);
    },
    [optionsUnit, formRef]
  );

  const submit = useCallback(
    async (data) => {
      if (selectedUnitType.value) data.unit = selectedUnitType.value;

      if (selectedType.value) data.type = selectedType.value;

      // FEAT-015: tamanho conforme o tipo (o backend valida de novo).
      data.photo_size = needsPhotoSize
        ? selectedPhotoSize?.value || null
        : null;

      data.spread_size = needsSpreadSize
        ? selectedSpreadSize?.value || null
        : null;

      // Qtde de fotos do álbum (só Fotolivro; obrigatória na forma ADICIONAL).
      data.photos_quantity =
        needsSpreadSize && data.photos_quantity
          ? Number(data.photos_quantity)
          : null;

      // FOTOLIVRO: os dois tamanhos (álbum) OU nenhum (adicional, com qtde).
      if (needsSpreadSize) {
        const hasSizes = !!data.photo_size && !!data.spread_size;
        const hasNoSizes = !data.photo_size && !data.spread_size;

        if (!hasSizes && !hasNoSizes) {
          formRef.current.setErrors({
            photo_size: !data.photo_size
              ? 'Álbum: informe os dois tamanhos'
              : undefined,
            spread_size: !data.spread_size
              ? 'Álbum: informe os dois tamanhos'
              : undefined,
          });
          return;
        }

        if (hasNoSizes && !data.photos_quantity) {
          formRef.current.setErrors({
            photos_quantity: 'Adicional de álbum: informe a qtde de fotos',
          });
          return;
        }
      }

      try {
        formRef.current.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().min(3, 'Nome mínimo 3 caracteres'),

          finish_type: Yup.string().min(3, 'Descrição mínimo 3 caracteres'),

          unit: Yup.string().required('Un. é obrigatório'),

          type: Yup.string().required('Tipo é obrigatório'),

          price: Yup.string().required('Preço é obrigatório'),

          photo_size: photoSizeRequired
            ? Yup.string()
                .nullable()
                .required('Tamanho da foto é obrigatório para este tipo')
            : Yup.string().nullable(),

          // Fotolivro: a dupla de tamanhos é validada acima (álbum OU adicional).
          spread_size: Yup.string().nullable(),

          photos_quantity: Yup.number()
            .typeError('Valor inválido')
            .positive('Valor inválido')
            .nullable(),
        });

        await schema.validate(data, { abortEarly: false });

        setLoading(true);

        data.price = await currencyToFloat(data.price);

        if (product.id) {
          const response = await update(URL, data);

          if (!response.error) setProduct(response);
        } else {
          const response = await store(URL, data);

          if (!response.error) history.push(`/${URL}/${response.id}`);
        }

        setLoading(false);
      } catch (error) {
        const errorMessages = {};

        if (error instanceof Yup.ValidationError) {
          error.inner.forEach((err) => {
            errorMessages[err.path] = err.message;
          });
          formRef.current.setErrors(errorMessages);
        }
      }
    },
    [
      getProduct,
      product,
      selectedUnitType,
      selectedType,
      selectedPhotoSize,
      selectedSpreadSize,
      needsPhotoSize,
      needsSpreadSize,
    ]
  );

  // Ativa/Inativa o produto (mesmo padrao de users): PUT reversivel com is_active.
  const setStatusProduct = useCallback(
    async (status) => {
      setLoading(true);

      const data = { ...product };

      data.is_active = status;
      data.price = await currencyToFloat(product.price);

      const response = await update(URL, data);

      if (!response.error) {
        response.price = await floatToCurrency(response.price);

        formRef.current.setFieldValue('price', response.price);

        setProduct(response);
      }

      setLoading(false);
    },
    [product]
  );

  const loadOptionsUnitType = useCallback(async () => {
    const response = await indexEnum('unit-types');

    setOptionsUnit(response);
  }, []);

  const loadOptionsType = useCallback(async () => {
    const response = await indexEnum('product-types');

    setOptionsType(response);
  }, []);

  useEffect(() => {
    const { id } = params;

    if (id) getProduct(id);

    loadOptionsUnitType();

    loadOptionsType();

    dispatch(
      setBreadcrumb([
        { page: 'produtos', url: '/products' },
        { page: id ? 'editar' : 'novo', url: null },
      ])
    );
  }, [params]);

  return (
    <Container className="container ">
      <div className="row mt-3 px-1 justify-content-center">
        <div className="col-12 text-center my-3">
          <h4>{product.id ? `EDITAR ${product.name}` : 'NOVO PRODUTO'}</h4>

          {product.id && (
            <div className="mt-2">
              {product.is_active === false ? (
                <StatusBadge $inactive>Inativo</StatusBadge>
              ) : (
                <StatusBadge>Ativo</StatusBadge>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="row justify-content-center mb-5">
        <Form
          ref={formRef}
          onSubmit={submit}
          initialData={product}
          noValidate
          className="col-12 col-lg-8 "
        >
          <GroupData>
            <div className="col-1 d-none">
              <InputLabel name="id" label="ID" />
            </div>

            <div className="row justify-content-center">
              <div className="col-12 col-md-8 mt-5 px-1">
                <InputLabel name="name" label="nome" />
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-12 col-md-8 mt-3 px-1">
                <InputLabel name="finish_type" label="Descrição" />
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-6 col-md-4 mt-3 px-1">
                <SelectLabel
                  name="type"
                  label="tipo"
                  options={optionsType}
                  placeholder=""
                  value={selectedType}
                  onChange={(e) => {
                    setSelectedType(e);
                    // Trocou o tipo: os tamanhos valem para tipos específicos.
                    setSelectedPhotoSize(null);
                    setSelectedSpreadSize(null);
                  }}
                />
              </div>

              <div className="col-6 col-md-2 mt-3 px-1">
                <SelectLabel
                  name="unit"
                  label="unidade"
                  options={optionsUnit}
                  placeholder=""
                  value={selectedUnitType}
                  onChange={(e) => setSelectedUnitType(e)}
                />
              </div>

              <div className="col-sm col-md-2 mt-3 px-1">
                <InputCurrencyLabel name="price" label="preço" />
              </div>
            </div>

            {(needsPhotoSize || needsSpreadSize) && (
              <div className="row justify-content-center">
                {needsPhotoSize && (
                  <div className="col-6 col-md-4 mt-3 px-1">
                    <SelectLabel
                      name="photo_size"
                      label={
                        photoSizeRequired
                          ? 'tamanho da foto'
                          : 'tamanho da foto (opcional)'
                      }
                      required={photoSizeRequired}
                      isClearable={!photoSizeRequired}
                      options={PHOTO_SIZES.map(toOption)}
                      placeholder=""
                      value={selectedPhotoSize}
                      onChange={(e) => setSelectedPhotoSize(e)}
                    />
                  </div>
                )}

                {needsSpreadSize && (
                  <div className="col-6 col-md-4 mt-3 px-1">
                    <SelectLabel
                      name="spread_size"
                      label="tamanho da lâmina"
                      isClearable
                      options={SPREAD_SIZES.map(toOption)}
                      placeholder=""
                      value={selectedSpreadSize}
                      onChange={(e) => setSelectedSpreadSize(e)}
                    />
                  </div>
                )}

                {needsSpreadSize && (
                  <div className="col-6 col-md-3 mt-3 px-1">
                    <InputLabel
                      name="photos_quantity"
                      label="qtde de fotos"
                      type="number"
                    />
                  </div>
                )}
              </div>
            )}
          </GroupData>

          <div className="row justify-content-center mt-4">
            <div className="col-6 col-md-4 col-xl-3">
              <button
                className="btn btn-outline-secondary btn-block col-12"
                type="button"
                onClick={() => history.push('/products')}
              >
                {product.id ? 'voltar' : 'cancelar'}
              </button>
            </div>

            {product.id && product.is_active !== false && (
              <div className="col-6 col-md-4 col-xl-3">
                <button
                  className="btn btn-danger btn-block col-12"
                  type="button"
                  onClick={() => setStatusProduct(false)}
                >
                  Inativar Produto
                </button>
              </div>
            )}

            {product.id && product.is_active === false && (
              <div className="col-6 col-md-4 col-xl-3">
                <button
                  className="btn btn-success btn-block col-12"
                  type="button"
                  onClick={() => setStatusProduct(true)}
                >
                  Ativar Produto
                </button>
              </div>
            )}

            <div className="col-6 col-md-4 col-xl-3">
              <button
                className="btn btn-primary btn-block col-12"
                type="submit"
              >
                {product.id ? 'atualizar' : 'salvar'}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </Container>
  );
};

Product.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default Product;

export const Container = styled.div`
  /* text-transform: uppercase; */

  height: 100vh;
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(props) => (props.$inactive ? '#ffffff' : '#1b5e20')};
  background-color: ${(props) => (props.$inactive ? '#d11515' : '#c8e6c9')};
`;
